import React from 'react';
import { DollarSign, Clock, Shield, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';

const SREDBasicsSection = () => {
  const benefits = [
    {
      icon: DollarSign,
      title: 'Up to 64% Recovery',
      description: 'CCPCs can recover up to 64% of eligible salary expenditures through combined federal and provincial credits.'
    },
    {
      icon: Clock,
      title: 'Cash Refunds',
      description: 'Qualifying CCPCs receive refundable tax credits—real cash back, not just tax deductions.'
    },
    {
      icon: Shield,
      title: 'Retroactive Claims',
      description: 'You can claim SR&ED credits up to 18 months after your fiscal year-end.'
    },
    {
      icon: TrendingUp,
      title: 'Annual Program',
      description: 'SR&ED is an ongoing program—claim every year you perform eligible innovation activities.'
    }
  ];

  const eligibleActivities = [
    'Developing new products or processes',
    'Improving existing products or processes',
    'Solving technical challenges and uncertainties',
    'Testing and experimentation',
    'Software development and integration',
    'Manufacturing process improvements',
    'Quality and efficiency optimisation',
    'Equipment and system modifications'
  ];

  return (
    <section id="sred-basics" className="relative bg-black py-24 lg:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1920&q=80" 
          alt="" 
          className="w-full h-full object-cover opacity-5"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 to-black/90" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <span className="text-[#4A90A4] text-sm font-semibold uppercase tracking-wider mb-4 block">Innovation Funding</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            What is <span className="text-[#4A90A4]">SR&ED?</span>
          </h2>
          <p className="text-white/60 text-lg leading-relaxed">
            The Scientific Research and Experimental Development (SR&ED) program is Canada's largest tax incentive for innovation. It provides billions of dollars annually to businesses performing research and development work in Canada.
          </p>
          <p className="text-white/60 text-lg leading-relaxed mt-4">
            <strong className="text-white">Here's the thing:</strong> Many companies are already doing work that qualifies—they just don't realise it. If you're solving problems, improving processes, or developing new ways of doing things, you may be leaving significant funding on the table.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <div
                key={index}
                className="group bg-white/[0.02] border border-white/10 p-6 hover:border-[#4A90A4]/50 hover:bg-[#4A90A4]/5 transition-all duration-500"
              >
                <div className="w-14 h-14 bg-[#4A90A4]/10 flex items-center justify-center mb-4 group-hover:bg-[#4A90A4] transition-colors duration-300">
                  <IconComponent size={28} className="text-[#4A90A4] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {benefit.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Eligible Activities + Rate Card */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left - Activities */}
          <div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
              Activities That May <span className="text-[#4A90A4]">Qualify</span>
            </h3>
            <p className="text-white/60 mb-8 leading-relaxed">
              Innovation happens in every industry. You don't need a white coat or a lab—if you're tackling technical challenges and developing new solutions, you may qualify.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {eligibleActivities.map((activity, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-white/[0.02] border border-white/5">
                  <CheckCircle size={18} className="text-[#4A90A4] mt-0.5 flex-shrink-0" />
                  <span className="text-white/70 text-sm">{activity}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Rate Card */}
          <div className="bg-gradient-to-br from-[#4A90A4]/10 to-transparent border border-[#4A90A4]/30 p-8">
            <h4 className="text-xl font-bold text-white mb-2">Potential Recovery Rates</h4>
            <p className="text-white/50 text-sm mb-8">For Canadian-Controlled Private Corporations (CCPCs)</p>
            
            <div className="space-y-8">
              <div>
                <div className="flex justify-between mb-3">
                  <span className="text-white font-medium">Innovation Salaries (T4)</span>
                  <span className="text-[#4A90A4] font-bold text-xl">Up to 64%</span>
                </div>
                <div className="h-3 bg-white/10 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#4A90A4] to-[#5BA0B4] transition-all duration-1000" style={{ width: '64%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-3">
                  <span className="text-white font-medium">Materials</span>
                  <span className="text-[#4A90A4] font-bold text-xl">Up to 42%</span>
                </div>
                <div className="h-3 bg-white/10 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#4A90A4] to-[#5BA0B4]" style={{ width: '42%' }} />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-3">
                  <span className="text-white font-medium">Canadian Contractors</span>
                  <span className="text-[#4A90A4] font-bold text-xl">Up to 32%</span>
                </div>
                <div className="h-3 bg-white/10 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#4A90A4] to-[#5BA0B4]" style={{ width: '32%' }} />
                </div>
              </div>
            </div>

            <p className="text-white/40 text-xs mt-8">
              Rates shown are maximum combined federal and provincial rates for CCPCs. Actual rates vary by province and company type.
            </p>

            <a
              href="#calculator"
              className="group mt-8 w-full bg-[#4A90A4] text-white py-4 font-medium flex items-center justify-center gap-3 hover:bg-[#5BA0B4] transition-all duration-300"
            >
              Calculate Your Potential Refund
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SREDBasicsSection;
